import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import type { UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Formik, useFormikContext } from 'formik';
import LogRocket from 'logrocket';
import { type Dispatch, type FC, type ReactElement, type SetStateAction, useContext } from 'react';

import { MAX_ATTACHMENT_BYTES } from '@core/constants/app';
import { SlabContext } from '@core/context/SlabContext';
import {
  type AttachmentFileFormikEntry,
  type EmailFormikType,
  EmailSchema,
  FormikEmail,
} from '@core/formik/email';
import { NewQuote, type Quote } from '@core/types/api/Quote/Quote';
import { SendMessageInput } from '@core/types/api/SendMessageInput/SendMessageInput';
import { base64DecodedByteCount } from '@core/utils/base64';
import { formatAttachmentBytes } from '@core/utils/emailAttachments';
import { mergeEmailDefaults } from '@core/utils/emailDefaults';
import {
  type EmailSendFailure,
  emailSendErrorMessage,
  isEmailConnectionError,
} from '@core/utils/emailSendErrors';
import { hasStringValue } from '@core/utils/equality';
import { hasFormikFieldsErrors } from '@core/utils/formikHelpers';
import { NIL_UUID } from '@core/utils/uuid';

import { type UpdateType, useSlabMutation } from '../../hooks/useSlabMutation';
import { useSlabQuery } from '../../hooks/useSlabQuery';
import type { MakeToastBuilderReturn } from '../../providers/ToastProvider/utils/makeToastBuilder';
import type { MutationRouteBarrelTypes } from '../../utils/ApiClient';
import type { QueryError } from '../../utils/Query';
import { getTenantRequiresApprovals } from '../../utils/TenantConfig';
import { ErrorFullPage } from '../Error/ErrorFullPage';
import { FOOTER_BUTTON_SX, SlabButton } from '../SlabButton/SlabButton';
import { DrawerCloseHandler } from '../SlabDrawer/DrawerCloseHandler';
import { SlabDrawer } from '../SlabDrawer/SlabDrawer';
import { SlabDrawerHeader } from '../SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '../SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '../SlabDrawer/useDrawerCloseGuard';
import { SlabStepper } from '../SlabStepper/SlabStepper';
import { useStepper } from '../SlabStepper/useStepper';
import { EmailAttachmentsSection } from './EmailAttachmentsSection';
import { EmailComposer } from './EmailComposer';
import { NylasStepSetUpTemplate, type NylasStepSetUpTemplateProps } from './NylasStepSetUpTemplate';

export const MarkAsSentAndShowToast = async ({
  quoteID,
  markAsSentMutation,
  toastHook,
}: {
  quoteID: string;
  markAsSentMutation: UseMutationResult<
    Quote,
    QueryError,
    UpdateType<MutationRouteBarrelTypes['POST mark quote as sent']['args']>,
    unknown
  >;
  toastHook: MakeToastBuilderReturn;
}): Promise<void> => {
  try {
    await markAsSentMutation.mutateAsync({
      args: { pathParams: { id: quoteID }, body: {} },
    });
  } catch (e) {
    if (e instanceof AxiosError && e.response?.status !== 422) {
      // Ignore status transition failure if 422: that HTTP status indicates that the tenant
      // has no "is sent" quote status, so the user shouldn't be expecting an auto-transition.
      toastHook.success({ message: 'Quote sent. Status update failed, update manually.' });
      return;
    }
  }
  toastHook.success({ message: 'Quote sent' });
};

export enum NylasDrawerSteps {
  COMPOSE_EMAIL = 'Compose email',
  ATTACH_FILES = 'Attach files',
  SET_UP_TEMPLATE = 'Set up template',
}

type NylasDrawerSectionsProps = Omit<NylasStepSetUpTemplateProps, 'onPdfChanged'> & {
  pdfFileName: string;
};

export const NylasDrawerSections: FC<NylasDrawerSectionsProps> = ({
  pdfFileName,
  quote,
  requiresQuoteApprovals,
}) => {
  const formikBag = useFormikContext<EmailFormikType>();

  // EmailAttachmentsSection merges batches with a functional update because its
  // file reads are async; `setValues` is the only Formik setter that takes one.
  const handleFilesChange: Dispatch<SetStateAction<AttachmentFileFormikEntry[]>> = (update) => {
    formikBag.setValues((prev) => ({
      ...prev,
      files: typeof update === 'function' ? update(prev.files) : update,
    }));
  };

  const { activeStep, changeStep, steps } = useStepper({
    initialStep: NylasDrawerSteps.COMPOSE_EMAIL,
    steps: [
      {
        label: NylasDrawerSteps.COMPOSE_EMAIL,
        content: <EmailComposer formikBag={formikBag} />,
        error: hasFormikFieldsErrors(formikBag, ['to', 'cc', 'bcc', 'subject', 'body']),
      },
      {
        label: NylasDrawerSteps.ATTACH_FILES,
        content: (
          <EmailAttachmentsSection
            pdfBase64={formikBag.values.file.file}
            pdfFileName={pdfFileName}
            additionalFiles={formikBag.values.files}
            onFilesChange={handleFilesChange}
          />
        ),
        error: hasFormikFieldsErrors(formikBag, ['files']),
      },
      {
        label: NylasDrawerSteps.SET_UP_TEMPLATE,
        content: (
          <NylasStepSetUpTemplate
            quote={quote}
            onPdfChanged={(pdfBase64): void => {
              formikBag.setFieldValue('file.file', pdfBase64);
            }}
            requiresQuoteApprovals={requiresQuoteApprovals}
          />
        ),
        error: hasFormikFieldsErrors(formikBag, ['file']),
        // Keep this step mounted even while another step is active so the quote
        // PDF is generated as soon as the drawer opens. Otherwise the default
        // attachment stays empty until the user manually visits this step.
        transitionProps: { unmountOnExit: false },
      },
    ],
  });

  return <SlabStepper steps={steps} activeStep={activeStep} onChangeStep={changeStep} />;
};

type Props = {
  quoteID: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onSuccess: () => void;
  onError: (message: string) => void;
  onClose?: () => void;
};
/**
 * @description The NylasDrawer component is like the different drawer
 * components used for creating/editing any of the other domain objects.
 * The difference is that the component doesn't create/modify an object
 * in the database, it sends data to the server that then relays that data
 * to Nylas to be sent as an email with the quote's PDF attached to the
 * email message.
 */
export const NylasDrawerPage: FC<Omit<Props, 'isOpen'>> = ({
  quoteID,
  setIsOpen: setIsOpenProp,
  onSuccess,
  onError,
  onClose,
}) => {
  const ctx = useContext(SlabContext);
  const { guardClose, editTracker } = useDrawerCloseGuard();

  const {
    data: quote,
    isLoading: isLoadingQuote,
    isError: isErrorQuote,
  } = useSlabQuery(
    'GET quote by ID',
    {
      pathParams: {
        id: quoteID,
      },
    },
    {
      enabled: quoteID !== NIL_UUID,
    },
  );

  const {
    data: quoteProducts,
    isLoading: isLoadingQuoteProducts,
    isError: isErrorQuoteProducts,
  } = useSlabQuery(
    'GET quote products by quote ID',
    {
      pathParams: { id: quote?.id ?? NIL_UUID },
    },
    {
      enabled: quote !== undefined,
    },
  );

  const {
    isLoading: isLoadingQuotePolicies,
    isError: isErrorQuotePolicies,
    data: quotePolicies,
  } = useSlabQuery('GET quote policies', {});

  const setIsOpen: Dispatch<SetStateAction<boolean>> = (value) => {
    setIsOpenProp(value);
    if (value === false) {
      onClose?.();
    }
  };

  const sendMessage = useSlabMutation('POST nylas v3 message', {
    onSuccess: () => {
      onSuccess();
      setIsOpen?.(false);
    },
    onError: (error) => {
      LogRocket.captureException(error);
      Sentry.captureException(error);
      const failure: EmailSendFailure = {
        status: error.response?.status,
        kind: error.response?.data?.kind,
      };
      onError(
        isEmailConnectionError(failure)
          ? 'Your email connection needs to be renewed. Reconnect it in Email Config and try again.'
          : emailSendErrorMessage(failure),
      );
    },
  });

  const isLoading =
    isLoadingQuote ||
    isLoadingQuoteProducts ||
    isLoadingQuotePolicies ||
    quotePolicies === undefined;
  const isError = isErrorQuote || isErrorQuoteProducts || isErrorQuotePolicies;

  if (isLoading || quoteID === NIL_UUID) {
    return null;
  }

  if (quote === undefined || isError) {
    return <ErrorFullPage />;
  }

  const quoteWithProducts = NewQuote({ ...quote, products: quoteProducts ?? [] });

  const initialValues = FormikEmail({
    to: quoteWithProducts.contacts
      .filter(
        (contact): contact is typeof contact & { emailAddress: string } =>
          contact.emailAddress !== null,
      )
      .map((contact) => ({
        name: `${contact.firstName} ${contact.lastName}`.trim(),
        email: contact.emailAddress,
      })),
    cc: mergeEmailDefaults(ctx.userInfo.tenant.defaultCc, ctx.userInfo.emailDefaults.defaultCc),
    bcc: mergeEmailDefaults(ctx.userInfo.tenant.defaultBcc, ctx.userInfo.emailDefaults.defaultBcc),
    file: {
      file: '',
      fileName: quoteWithProducts.fileName(),
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (message): Promise<void> => {
        const wireMessage = new SendMessageInput({ ...message, quoteId: quoteID });
        sendMessage.mutate({
          args: { body: wireMessage },
          schema: EmailSchema,
        });
      }}
      validationSchema={EmailSchema}
    >
      {(formikBag): ReactElement => {
        const totalAttachmentBytes =
          base64DecodedByteCount(formikBag.values.file.file) +
          formikBag.values.files.reduce((acc, f) => acc + base64DecodedByteCount(f.file), 0);
        const isOverSizeLimit = totalAttachmentBytes > MAX_ATTACHMENT_BYTES;
        // Sending is not offered from the discard prompt, so a stray click never sends the email.
        const requestClose = (): void =>
          guardClose({
            isDirty: editTracker.isEdited(),
            canSave: false,
            isSaving: sendMessage.isPending,
            onSave: formikBag.submitForm,
            onDiscard: () => setIsOpen(false),
          });

        return (
          <SlabDrawerLayout>
            <DrawerCloseHandler onClose={requestClose} editTracker={editTracker} />
            <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
              <SlabDrawerHeader title='Send Quote' onClose={requestClose} sx={{ mt: 2 }} />

              <NylasDrawerSections
                pdfFileName={quoteWithProducts.fileName()}
                quote={quoteWithProducts}
                requiresQuoteApprovals={getTenantRequiresApprovals({
                  tenant: ctx.userInfo.tenant,
                  quotePolicies,
                })}
              />
            </Box>

            <Box
              sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}
            >
              {isOverSizeLimit && (
                <Typography role='alert' variant='body2' color='error' sx={{ width: '100%' }}>
                  {`These attachments total ${formatAttachmentBytes(totalAttachmentBytes)}, over the ${formatAttachmentBytes(MAX_ATTACHMENT_BYTES)} limit, so this quote can't be sent. Remove a file and try again.`}
                </Typography>
              )}

              <SlabButton
                disabled={sendMessage.isPending}
                sx={FOOTER_BUTTON_SX}
                variant='slab-secondary'
                onClick={requestClose}
              >
                Cancel
              </SlabButton>
              <SlabButton
                sx={FOOTER_BUTTON_SX}
                disabled={
                  sendMessage.isPending ||
                  isOverSizeLimit ||
                  !hasStringValue(formikBag.values.file.file)
                }
                loading={sendMessage.isPending}
                variant='slab-primary'
                onClick={() => formikBag.handleSubmit()}
              >
                Send
              </SlabButton>
            </Box>
          </SlabDrawerLayout>
        );
      }}
    </Formik>
  );
};

export const NylasDrawer = (props: Props): ReactElement | null => {
  return (
    <SlabDrawer
      paperProps={{ sx: { width: '80rem', maxWidth: '100%' } }}
      isOpen={props.isOpen}
      close={() => props.setIsOpen(false)}
    >
      <NylasDrawerPage {...props} />
    </SlabDrawer>
  );
};
